import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "@repo/ui/lib/utils";
import { forwardRef } from "react";
import Icon, { IconName } from "@repo/ui/components/ui/icon/icon";

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  uncheckedIcon?: IconName;
  checkedIcon?: IconName;
}

const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, uncheckedIcon, checkedIcon, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "group shadow-toggle relative inline-flex h-[40px] w-[80px] shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-slate-300 dark:focus-visible:ring-offset-slate-950 bg-secondary-gray2",
      className,
    )}
    {...props}
    ref={ref}
  >
    {uncheckedIcon && checkedIcon && (
      <>
        <Icon
          icon={uncheckedIcon}
          className="fill-secondary-gray1 stroke-secondary-gray1 absolute top-1/2 -translate-y-1/2 left-[8px] h-[20px] w-[20px] transition-all group-data-[state=checked]:block group-data-[state=unchecked]:hidden"
        />
        <Icon
          icon={checkedIcon}
          className="fill-secondary-gray1 stroke-secondary-gray1 absolute top-1/2 -translate-y-1/2 right-[8px] h-[20px] w-[20px] transition-all group-data-[state=unchecked]:block group-data-[state=checked]:hidden"
        />
      </>
    )}

    <SwitchPrimitives.Thumb
      className={cn(
        "pointer-events-none block h-[40px] w-[40px] rounded-full bg-primary-magenta shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-[40px] data-[state=unchecked]:translate-x-[-2px]",
      )}
    >
      {uncheckedIcon && checkedIcon && (
        <>
          <Icon
            icon={uncheckedIcon}
            className="fill-primary-white stroke-primary-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[20px] w-[20px] transition-all group-data-[state=unchecked]:block group-data-[state=checked]:hidden"
          />
          <Icon
            icon={checkedIcon}
            className="fill-primary-white stroke-primary-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[20px] w-[20px] transition-all group-data-[state=unchecked]:hidden group-data-[state=checked]:block"
          />
        </>
      )}
    </SwitchPrimitives.Thumb>
  </SwitchPrimitives.Root>
));

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
